import React, { FC, useState } from 'react';
import { Trans } from 'react-i18next';
import { compose } from 'recompose';
import { Paper, Grid, Button, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Network } from 'api';
import { connectionPopup } from 'helpers';
import { useCurrentUser } from 'hooks';
import { CustomSVG } from 'components';
import { withSocialConnections, IWithSocialConnections } from 'providers/queries';
import Cookies from 'cookies';

interface IPropsEnhanced extends WithStyles<typeof styles>, IWithSocialConnections {}

const CONNECTION_CTA_VALUE = 'dismissed';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: `${theme.spacing(4)}px ${theme.spacing(10)}px ${theme.spacing(2)}px`,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    container: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    socialButton: {
      borderRadius: 4,
      color: theme.palette.common.white,
    },
    twitter: {
      backgroundColor: theme.custom.networks.twitter,
      '&:hover': {
        backgroundColor: theme.custom.networks.twitter,
      },
    },
    linkedIn: {
      backgroundColor: theme.custom.networks.linkedin,
      '&:hover': {
        backgroundColor: theme.custom.networks.twitter,
      },
    },
    buttonDismiss: {
      margin: '0 auto',
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withSocialConnections('none'), withStyles(styles));

const Welcome: FC<IPropsEnhanced> = ({ classes, connections }) => {
  const isDismissed = Cookies.connectionCta === CONNECTION_CTA_VALUE;

  const { id } = useCurrentUser();
  const [dismissed, setDismissed] = useState(isDismissed);

  if (dismissed || connections.length) {
    return null;
  }

  function handleConnectTwitter() {
    connectionPopup(id, Network.Twitter);
  }

  function handleConnectLinkedIn() {
    connectionPopup(id, Network.Linkedin);
  }

  function handleDismiss() {
    Cookies.connectionCta = CONNECTION_CTA_VALUE;
    setDismissed(true);
  }

  const twitterButtonClasses = classNames(classes.socialButton, classes.twitter);
  const linkedInButtonClasses = classNames(classes.socialButton, classes.linkedIn);

  return (
    <Paper className={classes.root}>
      <Typography align="center" variant="subtitle2">
        <Trans i18nKey="onboarding:timelineConnections" />
      </Typography>
      <Grid className={classes.container} container alignItems="center" justify="space-evenly">
        <Grid item>
          <Button
            variant="contained"
            className={twitterButtonClasses}
            onClick={handleConnectTwitter}
            startIcon={<CustomSVG name="twitterOutline" size={20} viewBox="0 0 21 20" />}>
            <Trans i18nKey="user:connectTwitter" />
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            className={linkedInButtonClasses}
            onClick={handleConnectLinkedIn}
            startIcon={<CustomSVG name="linkedinOutline" size={20} />}>
            <Trans i18nKey="user:connectLinkedIn" />
          </Button>
        </Grid>
      </Grid>
      <div className={classes.buttonDismiss}>
        <Button fullWidth onClick={handleDismiss}>
          <Trans i18nKey="onboarding:timelineDismiss" />
        </Button>
      </div>
    </Paper>
  );
};

export default enhancers(Welcome);
